import React from 'react';
import { Card, Badge, BlockStack, Bleed, Box, Button } from '@shopify/polaris';
import { StudioVideoStatus, type StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';
import { useVideoStatus } from '../studio/hooks/useVideoStatus';
import VideoCardContent, { VideoCardContentOptions } from './VideoCardContent';
import VideoMetadata from './VideoMetadata';
import LiveBadge from './LiveBadge';
import { ShareVideoButton } from '@/features/publish/ShareVideoButton';

interface VideoCardProps {
  video: StudioVideo;
  options?: VideoCardContentOptions;
  hideActions?: boolean;
  showProgressBannerWarning?: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({
  video,
  options,
  hideActions = false,
  showProgressBannerWarning = false,
}) => {
  const { status, hasError, progress, isProcessing } = useVideoStatus(video);
  const isVideoReady = !hasError && !isProcessing && status !== StudioVideoStatus.DRAFT;
  const isDraft = status === StudioVideoStatus.DRAFT;

  return (
    <Box maxWidth="300px">
      <Card>
        <Bleed marginInline="400" marginBlock="400">
          <div className="relative flex aspect-[9/16] min-h-[310px] w-full flex-col justify-between overflow-hidden">
            <VideoCardContent
              video={video}
              status={status}
              progress={progress}
              hasError={hasError}
              options={options}
              showProgressBannerWarning={showProgressBannerWarning}
            />
            <LiveBadge video={video} />

            {video.autoGenerated && (
              <div className="absolute right-2 top-2">
                <Badge
                  tone="magic"
                  toneAndProgressLabelOverride="Auto-generated: Video asset was generated automatically."
                >
                  Auto-generated
                </Badge>
              </div>
            )}
          </div>

          {!hideActions && (
            <Box background="bg-surface-secondary" padding="400">
              <BlockStack gap="400">
                <VideoMetadata video={video} />

                <ShareVideoButton video={video} disabled={!isVideoReady} isDraft={isDraft} />
              </BlockStack>
            </Box>
          )}
        </Bleed>
      </Card>
    </Box>
  );
};

export default VideoCard;
